import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Chip,
  Stack,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import { userNoteService } from '../services/userNoteService';
import { UserNote, CreateUserNoteRequest } from '../types/userNote';
import { useAuth } from '../contexts/AuthContext';
import { formatServerDateTime } from '../utils/dateUtils';
// [3. javító kör, F-1, 2026-10-03] lejárat-tudatos burkolók (`utils/saveErrors.ts`): a megjegyzés
// MENTÉSÉNEK 401-e zárral a „megmaradtak" mondatot adja (a nyers „… (HTTP 401)" helyett).
import { requestErrorMessage, saveErrorMessage } from '../utils/saveErrors';
import { useFeedback } from '../contexts/FeedbackContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';

interface UserNotesProps {
  userId: string;
  userRoleName: string; // To determine if notes should be shown
  /**
   * A beágyazó DIALÓGUS (`UserProfileDialog`) saját bezárás-kérdéséhez: jelzi, hogy van-e
   * beírt, el nem küldött megjegyzés. Unmountkor `false`-t küld.
   */
  onDirtyChange?: (dirty: boolean) => void;
}

const UserNotes: React.FC<UserNotesProps> = ({ userId, userRoleName, onDirtyChange }) => {
  const { user } = useAuth();
  const { showSuccess } = useFeedback();
  const confirm = useConfirm();
  const isAdmin = user?.userRole === 'Administrator';
  const isCoach = user?.userRole === 'Coach';
  const canViewNotes = isAdmin || isCoach;

  const [notes, setNotes] = useState<UserNote[]>([]);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  /**
   * [Web UX 2. csomag – mentetlen-védelem (8.1), 2026-10-03] A beírt, még el nem küldött
   * megjegyzés mentetlen adat. Lelet: `UserNotes.tsx:164-168` (korábbi sorok) — az Adatlap
   * fülről való elváltás és a beágyazó dialógus bezárása kérdés nélkül eldobta.
   * Piszkos = nem üres (trim után) a vázlat; az üres mező NEM kérdez.
   * A hook a korai `return null` ELŐTT áll (a hookok sorrendje nem függhet a szereptől).
   */
  const draftDirty = canViewNotes && newNoteContent.trim() !== '';
  useUnsavedChanges(draftDirty, 'Új megjegyzés');
  useEffect(() => {
    onDirtyChange?.(draftDirty);
  }, [draftDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  useEffect(() => {
    if (canViewNotes) {
      loadNotes();
    }
  }, [userId, canViewNotes]);

  // Don't show notes section for players or if user is not admin/coach
  if (!canViewNotes) {
    return null;
  }

  const loadNotes = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await userNoteService.getUserNotes(userId);
      setNotes(data);
    } catch (err: any) {
      console.error('Error loading notes:', err);
      setError(requestErrorMessage(err, 'Hiba történt a megjegyzések betöltésekor'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) {
      setError('A megjegyzés tartalma nem lehet üres');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const data: CreateUserNoteRequest = {
        content: newNoteContent.trim(),
      };
      await userNoteService.createUserNote(userId, data);
      setNewNoteContent('');
      showSuccess('A megjegyzés hozzáadva.');
      await loadNotes();
    } catch (err: any) {
      console.error('Error creating note:', err);
      setError(saveErrorMessage(err, 'Hiba történt a megjegyzés létrehozásakor'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!(await confirm({
      title: 'Megjegyzés törlése',
      message: 'Biztosan törölni szeretnéd ezt a megjegyzést?',
      confirmLabel: 'Törlés',
      destructive: true,
    }))) {
      return;
    }

    setError('');
    try {
      await userNoteService.deleteUserNote(noteId);
      showSuccess('A megjegyzés törölve.');
      await loadNotes();
    } catch (err: any) {
      console.error('Error deleting note:', err);
      setError(requestErrorMessage(err, 'Hiba történt a megjegyzés törlésekor'));
    }
  };

  const handleToggleAcknowledgment = async (note: UserNote) => {
    setError('');
    try {
      if (note.isAcknowledged) {
        // Remove acknowledgment
        await userNoteService.removeAcknowledgment(note.userNoteId);
        showSuccess('A láttamozás eltávolítva.');
      } else {
        // Add acknowledgment
        await userNoteService.acknowledgeUserNote(note.userNoteId);
        showSuccess('A megjegyzés láttamozva.');
      }
      await loadNotes();
    } catch (err: any) {
      console.error('Error toggling acknowledgment:', err);
      setError(requestErrorMessage(err, 'Hiba történt a láttamozás során'));
    }
  };

  // [Web UX 1. csomag (4.3), 2026-10-03] a `createdDate`/`acknowledgedDate` UTC-ben jön, de az alak
  // végpontonként eltér (validátor-lelet, élő API-n mérve): a GET-lista (`UserNotesController.cs:118`,
  // `:126`, DB-ből olvasva) „Z” NÉLKÜL, a POST `/acknowledge` válasza (`:293`, a memóriabeli
  // `DateTime.UtcNow` a `:278`-ból) „Z”-VEL. A felület a láttamozás VÁLASZÁT nem jeleníti
  // meg (a `handleToggleAcknowledgment` `:130`-ban eldobja), hanem újratölti a listát (`loadNotes`),
  // így itt MINDIG a GET-lista „Z” nélküli alakja jelenik meg; a „Z”-s alak kezelése tartalék — a közös segéd
  // (`formatServerDateTime` → `parseServerDateTime`) MINDKETTŐT helyesen budapesti időre váltja
  // (zónás szöveget változatlanul hagy, zóna nélkülihez UTC-t feltételez).
  // Értelmezhetetlen bemenetre a nyers szöveg marad (mint a régi `catch` ágban).
  const formatDate = (dateString: string) => formatServerDateTime(dateString, { fallback: dateString ?? '' });

  return (
    <Box sx={{ mt: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Megjegyzések (Edző/Admin számára)
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Ezek a megjegyzések csak az edzők és adminisztrátorok számára láthatóak. A játékos nem láthatja őket.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Add New Note */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Új megjegyzés hozzáadása"
            placeholder="Írj megjegyzést erről a játékosról..."
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            disabled={saving}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <AddIcon />}
            onClick={handleAddNote}
            disabled={saving || !newNoteContent.trim()}
          >
            {saving ? 'Mentés...' : 'Megjegyzés hozzáadása'}
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Notes List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : notes.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
            Még nincsenek megjegyzések
          </Typography>
        ) : (
          <Stack spacing={2}>
            {notes.map((note) => (
              <Card key={note.userNoteId} variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" color="primary">
                        {note.createdByName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(note.createdDate)}
                      </Typography>
                    </Box>
                    {isAdmin && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteNote(note.userNoteId)}
                        title="Megjegyzés törlése"
                        // [Web UX 9 / T5, 2026-10-13] UX-lista 6.1: a `title` önmagában nem megbízható név → aria-label.
                        aria-label="Megjegyzés törlése"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                    {note.content}
                  </Typography>

                  {/* Acknowledgments */}
                  {note.acknowledgments.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Láttamozták:
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {note.acknowledgments.map((ack) => (
                          <Chip
                            key={ack.userNoteAcknowledgmentId}
                            label={`${ack.acknowledgedByName} (${formatDate(ack.acknowledgedDate)})`}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ mb: 0.5 }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </CardContent>

                {/* Acknowledge Button */}
                {note.canAcknowledge && (
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={note.isAcknowledged ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                      onClick={() => handleToggleAcknowledgment(note)}
                      color={note.isAcknowledged ? 'success' : 'primary'}
                    >
                      {note.isAcknowledged ? 'Láttamozás visszavonása' : 'Láttamozom'}
                    </Button>
                  </CardActions>
                )}
              </Card>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default UserNotes;
